import React, { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CSVLink } from "react-csv";
import API from "../../services/api";
import { Toast } from "../../shared/Toast";
import { copyText, toLakhs, languageOptions } from "../../utils/constants";

const AdCodeStats = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adPartners, setAdPartners] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const response = await API({
        url: "/adresponses/adCodeStats",
        method: "POST",
        data: {
          date: selectedDate,
          lang_id: selectedLanguage ? selectedLanguage.value : null,
        },
      });

      if (response.status !== "SUCCESS") {
        setLoading(false);
        Toast("error", "Failed To Get Data");
        return;
      }

      const transformed = response.data?.flatMap((item) =>
        item.ad_partner_stats.length === 0
          ? [
              {
                date: item.date,
                lang_id: item.lang_id,
                ad_code: item.ad_code,
                ad_partner_name: null,
                ad_partner_id: null,
                request: 0,
                impressions: 0,
                clickhouse_request: 0,
                clickhouse_display: 0,
              },
            ]
          : item.ad_partner_stats.map((stat) => ({
              date: item.date,
              lang_id: item.lang_id,
              ad_code: item.ad_code,
              ad_partner_name: stat.ad_partner_name,
              ad_partner_id: stat.ad_partner_id,
              request: stat.request,
              impressions: stat.display,
              clickhouse_request: stat.clickhouse_request || 0,
              clickhouse_display: stat.clickhouse_display || 0,
            }))
      );

      setData(transformed);
      setLoading(false);
    };

    getData();
  }, [selectedDate, selectedLanguage]);

  useEffect(() => {
    const fetchAdPartners = async () => {
      const result = await API({
        url: "/adpartners/getAdPartnersNames",
        method: "POST",
      });
      if (result.status === "SUCCESS") {
        setAdPartners(result.data);
      }
    };
    fetchAdPartners();
  }, []);

  const startEdit = (item) => {
    const key = `${item.ad_code}-${item.lang_id}`;
    setEditingId(key);
    setEditingPartner(item.ad_partner_id ? { value: item.ad_partner_id, label: item.ad_partner_name } : null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingPartner(null);
  };

  const saveEdit = async (item) => {
    const newPartnerId = editingPartner?.value || null;
    const originalItem = data.find((d) => d.ad_code === item.ad_code && d.lang_id === item.lang_id);

    if (originalItem.ad_partner_id === newPartnerId) {
      cancelEdit();
      return;
    }

    const payload = {
      ad_code: item.ad_code,
      lang_id: item.lang_id,
      ad_partner_id: newPartnerId,
    };

    const result = await API({
      url: "/adresponses/editAdCode",
      method: "POST",
      data: payload,
    });

    if (result.status === "SUCCESS") {
      Toast("success", "Ad Partner updated successfully");
      setData((prev) =>
        prev.map((d) =>
          d.ad_code === item.ad_code && d.lang_id === item.lang_id
            ? {
                ...d,
                ad_partner_id: newPartnerId,
                ad_partner_name: editingPartner?.label || null,
              }
            : d
        )
      );
      cancelEdit();
    } else {
      Toast("error", result.message || "Failed to update Ad Partner");
    }
  };

  const csvHeaders = [
    { label: "Language", key: "lang_id" },
    { label: "Ad Code", key: "ad_code" },
    { label: "Ad Partner Name", key: "ad_partner_name" },
    { label: "Requests ", key: "clickhouse_request" },
    { label: "Impressions ", key: "clickhouse_display" },
    { label: "Requests (C)", key: "request" },
    { label: "Impressions (C)", key: "impressions" },
  ];

  const getLangName = (id) => {
    const found = languageOptions.find((l) => l.value == id);
    return found ? found.label : id;
  };

  const getRowKey = (item) => `${item.ad_code}-${item.lang_id}`;

  return (
    <div className="w-full mx-auto mt-10 p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
      <h2 className="text-3xl font-semibold text-gray-800 mb-8 text-center tracking-tight">Ad Code Stats</h2>

      <div className="flex flex-col md:flex-row justify-between gap-6 mb-8">
        <div className="flex flex-col w-full md:w-1/3">
          <label className="text-gray-600 text-sm mb-2">Select Date</label>
          <DatePicker selected={selectedDate} onChange={(d) => setSelectedDate(d)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" dateFormat="yyyy-MM-dd" />
        </div>

        <div className="flex flex-col w-full md:w-1/3">
          <label className="text-gray-600 text-sm mb-2">Select Language</label>
          <Select options={languageOptions} value={selectedLanguage} onChange={setSelectedLanguage} placeholder="Choose language" isClearable className="text-sm" />
        </div>

        <div className="flex flex-col justify-center text-gray-700 text-sm w-full md:w-1/3 text-center">
          Viewing stats for <strong>{selectedLanguage ? selectedLanguage.label : "All"}</strong> on <strong>{selectedDate.toDateString()}</strong>
        </div>
      </div>

      {data.length > 0 && (
        <div className="flex justify-end mb-4">
          <CSVLink data={data} headers={csvHeaders} filename={`ad_code_stats_${selectedDate.toISOString().slice(0, 10)}.csv`} className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700">
            Download CSV
          </CSVLink>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg shadow-sm">
        {loading ? (
          <div className="text-center p-10 text-gray-500">Loading...</div>
        ) : data.length === 0 ? (
          <div className="text-center p-10 text-gray-500">No data available.</div>
        ) : (
          <table className="min-w-full text-sm text-gray-700">
            <thead className="bg-gray-50 text-xs uppercase border-b">
              <tr>
                <th className="px-6 py-3 text-left">Language</th>
                <th className="px-6 py-3 text-left">Ad Code</th>
                <th className="px-6 py-3 text-left">Ad Partner Name</th>
                <th className="px-6 py-3 text-right text-blue-600" title="Our Requests">Requests (W2N) </th>
                <th className="px-6 py-3 text-right text-indigo-600" title="Our Impressions">Impressions (W2N) </th>
                <th className="px-6 py-3 text-right text-green-600" title="Client Request">Requests (Client)</th>
                <th className="px-6 py-3 text-right text-purple-600" title="Client Impressions">Impressions (Client)</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {data.map((item, idx) => {
                const rowKey = getRowKey(item);
                const isEditing = editingId === rowKey;

                return (
                  <tr key={idx}>
                    <td className="px-6 py-3">{getLangName(item.lang_id)}</td>

                    <td className="px-6 py-3 font-mono text-xs flex gap-2 break-all flex-wrap items-center justify-center text-center">
                      {item.ad_code}
                      <button onClick={() => copyText(item.ad_code)} className="text-indigo-500 text-xs hover:underline">
                        Copy
                      </button>
                    </td>

                    <td className="px-6 py-3">
                      {isEditing ? (
                        <Select options={adPartners} value={editingPartner} onChange={setEditingPartner} placeholder="Select Partner" isClearable className="w-48 text-sm" menuPortalTarget={document.body} />
                      ) : (
                        <span className={item.ad_partner_name ? "" : "text-gray-400"}>{item.ad_partner_name || "Not Assigned"}</span>
                      )}
                    </td>

                    <td className="px-6 py-3 text-right" title={item.clickhouse_request}>
                      {toLakhs(item.clickhouse_request)}
                    </td>
                    <td className="px-6 py-3 text-right" title={item.clickhouse_display}>
                      {toLakhs(item.clickhouse_display)}
                    </td>
                    <td className="px-6 py-3 text-right" title={item.request}>
                      {toLakhs(item.request)}
                    </td>
                    <td className="px-6 py-3 text-right" title={item.impressions}>
                      {toLakhs(item.impressions)}
                    </td>

                    <td className="px-6 py-3 text-left">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(item)} className="text-green-600 hover:text-green-800 text-xs font-medium">
                            Save
                          </button>
                          <button onClick={cancelEdit} className="text-red-600 hover:text-red-800 text-xs font-medium">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(item)} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdCodeStats;
